<template>
  <div class="space-y-4">
    <h2 class="mb-5 text-center text-lg font-medium text-[var(--plugin-charcoal)]">
      {{ t('plugin.employeesStep.title') }}
    </h2>
    <p v-if="loading" class="text-center text-[var(--plugin-gray-500)]">{{ t('plugin.loading') }}</p>
    <p v-else-if="error" class="text-center text-red-600">{{ error }}</p>
    <div v-else class="grid grid-cols-2 gap-4 sm:grid-cols-3">
      <button
        type="button"
        class="flex flex-col items-center justify-center rounded-[12px] border-2 bg-[var(--plugin-gray-100)] px-4 py-5 text-center transition"
        :class="state.employee === null ? 'border-primary' : 'border-transparent hover:border-[var(--plugin-border)]'"
        @click="select(null)"
      >
        <div
          class="flex h-10 w-10 items-center justify-center rounded-full bg-card text-primary"
          aria-hidden
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </div>
        <span class="mt-3 text-sm font-medium text-[var(--plugin-charcoal)]">
          {{ t('plugin.employeesStep.anyEmployee') }}
        </span>
        <span class="mt-1 text-xs text-[var(--plugin-gray-500)]">
          {{ t('plugin.employeesStep.firstAvailable') }}
        </span>
      </button>
      <button
        v-for="emp in employees"
        :key="emp.id"
        type="button"
        class="flex flex-col items-center justify-center rounded-[12px] border-2 bg-[var(--plugin-gray-100)] px-4 py-5 text-center transition"
        :class="state.employee?.id === emp.id ? 'border-primary' : 'border-transparent hover:border-[var(--plugin-border)]'"
        @click="select(emp)"
      >
        <div
          class="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--plugin-charcoal)] text-xs font-medium text-white"
          aria-hidden
        >
          {{ initials(emp) }}
        </div>
        <span class="mt-3 text-sm font-medium text-[var(--plugin-charcoal)]">
          {{ emp.surname ? `${emp.name} ${emp.surname}` : emp.name }}
        </span>
        <span class="mt-1 text-xs text-[var(--plugin-charcoal)]">
          {{ t('plugin.employeesStep.employee') }}
        </span>
        <span v-if="ratingText(emp.rating) !== null" class="mt-1 flex items-center justify-center gap-1 text-xs text-[var(--plugin-gray-500)]">
          <span class="text-primary">★</span>
          {{ ratingText(emp.rating) }}
        </span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { getEmployees } from '@/api/public'
import type { PublicEmployee, PublicReservationProduct } from '@/types/api'
import { useWizardState, setEmployee } from '@/composables/useWizardState'
import { t } from '@/i18n'

const props = defineProps<{
  branchId: number
  product: PublicReservationProduct | null
  selectedDate: string | null
  selectedTime: string | null
}>()

const state = useWizardState()
const employees = ref<PublicEmployee[]>([])
const loading = ref(true)
const error = ref<string | null>(null)

function ratingText(rating: unknown): string | null {
  if (rating == null) return null
  const n = typeof rating === 'number' ? rating : Number(rating)
  return Number.isFinite(n) ? n.toFixed(1) : null
}

function initials(emp: PublicEmployee): string {
  const n = (emp.name ?? '').trim()
  const s = (emp.surname ?? '').trim()
  if (n && s) return `${n[0]}${s[0]}`.toUpperCase()
  if (n) return n.slice(0, 2).toUpperCase()
  return '?'
}

function select(emp: PublicEmployee | null) {
  setEmployee(emp)
}

watch(
  () => [props.branchId, props.product, props.selectedDate, props.selectedTime] as const,
  async ([branchId, product, selectedDate, selectedTime]) => {
    const isServiceOrPackage = product?.type === 'service' || product?.type === 'package'
    if (!branchId || !product || !isServiceOrPackage) {
      employees.value = []
      loading.value = false
      error.value = null
      return
    }

    const slot =
      selectedDate && selectedTime
        ? `${selectedDate} ${selectedTime.length === 5 ? selectedTime : selectedTime.slice(0, 5)}`
        : undefined

    loading.value = true
    error.value = null
    try {
      employees.value = await getEmployees({
        branch_id: branchId,
        service_id: product.type === 'service' ? product.id : undefined,
        package_id: product.type === 'package' ? product.id : undefined,
        ...(slot ? { slot } : {}),
      })
    } catch (e) {
      error.value = e instanceof Error ? e.message : t('plugin.error.load')
      employees.value = []
    } finally {
      loading.value = false
    }
  },
  { immediate: true }
)
</script>
